import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { fetchContentBundle } from "../api/content";
import { ContentContext, type ContentStatus } from "./ContentContext";
import type { ContentBundle, FastProfileQuestion } from "../types";

/**
 * Mounted once, at the app root (`App.tsx`), so the fetch starts on the very
 * first render — which in practice is the Welcome screen, well before the
 * Fast Profile's own two-minute window opens. No component below this one
 * needs to trigger its own prefetch.
 *
 * There is deliberately no client-side fallback copy anywhere in this
 * provider or its consumers: a bundle that fails to load means explainer
 * affordances are simply absent (plan and refinement pages render in full
 * regardless), or, on the questionnaire specifically, a retry is shown by
 * the page itself rather than by this provider — see
 * `docs/design/features/W4-01.md` section "States".
 */
export function ContentProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<ContentStatus>("loading");
  const [bundle, setBundle] = useState<ContentBundle | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchContentBundle()
      .then((value) => {
        if (cancelled) return;
        setBundle(value);
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const explainer = useCallback(
    (key: string | undefined) => (key && bundle ? bundle.explainers[key] : undefined),
    [bundle],
  );

  const questionsByField = useMemo(() => {
    const map: Record<string, FastProfileQuestion> = {};
    for (const question of bundle?.fast_profile.questions ?? []) {
      map[question.field] = question;
    }
    return map;
  }, [bundle]);

  const retry = useCallback(() => {
    setStatus("loading");
    setAttempt((current) => current + 1);
  }, []);

  const value = useMemo(
    () => ({ status, bundle, explainer, questionsByField, retry }),
    [status, bundle, explainer, questionsByField, retry],
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}
